'use client';

import { useState, useEffect } from 'react';
import './DataTable.css';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import ChatHistoryModal from './ChatHistoryModal';

const DataTable = ({ logs }) => {
  const [sortField, setSortField] = useState('timestamp');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState('');
  const [modalTitle, setModalTitle] = useState('');
  const [chatModalOpen, setChatModalOpen] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [isRTL, setIsRTL] = useState(false);

  const handleSort = field => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedLogs = [...logs].sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];

    if (sortField === 'timestamp') {
      aValue = new Date(aValue);
      bValue = new Date(bValue);
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedLogs.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(logs.length / itemsPerPage);

  const formatDate = dateString => {
    return new Date(dateString).toLocaleString();
  };

  const truncateText = (text, maxLength = 1000) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const getStatusClass = status => {
    switch (status) {
      case 'success':
        return 'status-success';
      case 'error':
        return 'status-error';
      case 'pending':
        return 'status-pending';
      default:
        return '';
    }
  };

  const toggleDropdown = logId => {
    setOpenDropdown(openDropdown === logId ? null : logId);
  };

  const deleteClientLog = async logId => {
    if (window.confirm('Are you sure you want to delete this log?')) {
      try {
        // Add your delete API call here
        console.log('Deleting log:', logId);
        // You can add actual delete functionality here
      } catch (error) {
        console.error('Error deleting log:', error);
      }
    }
    setOpenDropdown(null);
  };

  useEffect(() => {
    const handleClickOutside = event => {
      if (!event.target.closest('.dropdown')) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const truncateLLMText = (text, maxLength = 100) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const openModal = (content, title, isRTL) => {
    setModalContent(content);
    setModalTitle(title);
    setIsRTL(isRTL);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalContent('');
    setModalTitle('');
    setIsRTL(false);
  };

  const openChatHistory = sessionId => {
    setSelectedSessionId(sessionId);
    setChatModalOpen(true);
  };

  const closeChatHistory = () => {
    setChatModalOpen(false);
    setSelectedSessionId(null);
  };

  return (
    <div className="data-table-container">
      <div className="table-info">
        <span>
          Showing {currentItems.length} of {logs.length} records
        </span>
      </div>

      <div className="table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('sessionId')}>
                SessionId {sortField === 'sessionId' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('timestamp')}>
                Date / Time {sortField === 'timestamp' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('case')}>
                Case {sortField === 'case' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('spokenText')}>
                LLM Input {sortField === 'spokenText' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('userInput')}>
                User Input {sortField === 'userInput' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('aiResponse')}>
                Response {sortField === 'aiResponse' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('totalResponseTime')}>
                LLM Response Time {sortField === 'totalResponseTime' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('client_url')}>
                Client URL {sortField === 'client_url' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('status')}>
                Status {sortField === 'status' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('error')}>
                Error Log {sortField === 'error' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {currentItems.map(log => (
              <tr key={log._id}>
                <td>
                  {
                    <button
                      className="btn btn-link p-0"
                      onClick={() => openChatHistory(log.sessionId)}
                      title="Click to view conversation history"
                    >
                      {log.sessionId}
                    </button>
                  }
                </td>
                <td>{formatDate(log.timestamp)}</td>
                <td title={log.case}>{log.case}</td>
                <td title={log.spokenText}
                  style={{ direction: log.micLanguage && log.micLanguage.toLowerCase().startsWith('he') ? 'rtl' : 'ltr' }}>
                  <div className="llm-text-container">
                    <span style={{ direction: log.micLanguage && log.micLanguage.toLowerCase().startsWith('he') ? 'rtl' : 'ltr' }}>{truncateLLMText(log.spokenText, 100)}</span>
                    {log.spokenText && log.spokenText.length > 100 && (
                      <span
                        className="read-more-btn"
                        onClick={() => openModal(log.spokenText, 'LLM Input', log.micLanguage && log.micLanguage.toLowerCase().startsWith('he'))}
                      >
                        Read More
                      </span>
                    )}
                  </div>
                </td>
                <td title={log.userInput} style={{ direction: log.micLanguage && log.micLanguage.toLowerCase().startsWith('he') ? 'rtl' : 'ltr' }}>{truncateText(log.userInput)}</td>
                <td title={log.aiResponse} style={{ direction: log.micLanguage && log.micLanguage.toLowerCase().startsWith('he') ? 'rtl' : 'ltr' }}>{truncateText(log.aiResponse)}</td>
                <td title={log.totalResponseTime}>{truncateText(log.totalResponseTime)}</td>
                <td>
                  {log.client_url && (
                    <a href={log.client_url} target="_blank" rel="noopener noreferrer">
                      {truncateText(log.client_url, 30)}
                    </a>
                  )}
                </td>
                <td>
                  <span className={`status-badge ${getStatusClass(log.status)}`}>{log.status}</span>
                </td>
                <td title={log.error} className="error-cell">
                  {log.error && <span className="error-text">{truncateText(log.error)}</span>}
                </td>
                <td className="action-cell">
                  <div className="dropdown">
                    <button
                      className="dropdown-toggle"
                      onClick={() => toggleDropdown(log._id)}
                      type="button"
                    >
                      <span className="dots">
                        <span></span>
                        <span></span>
                        <span></span>
                      </span>
                    </button>
                    {openDropdown === log._id && (
                      <div className="dropdown-menu show">
                        <a
                          className="dropdown-item"
                          href={`/add-client/${log?.user?._id || log?.user}`}
                        >
                          <div className="dropdown-icon profile-icon">👤</div>
                          <span>Profile</span>
                        </a>
                        <a
                          className="dropdown-item"
                          href={`/view-avatar-settings/${log?.user?._id || log?.user}`}
                        >
                          <div className="dropdown-icon human-icon">🤖</div>
                          <span>Human settings</span>
                        </a>
                        <a
                          className="dropdown-item"
                          href={`/view-button-settings/${log?.user?._id || log?.user}`}
                        >
                          <div className="dropdown-icon button-icon">⚙️</div>
                          <span>Button Settings</span>
                        </a>
                        <a
                          className="dropdown-item"
                          href={`/view-splash-settings/${log?.user?._id || log?.user}`}
                        >
                          <div className="dropdown-icon splash-icon">🎨</div>
                          <span>Splash Setting</span>
                        </a>
                        <a
                          className="dropdown-item"
                          href={`/global-settings/${log?.user?._id || log?.user}`}
                        >
                          <div className="dropdown-icon global-icon">🌐</div>
                          <span>Global Settings</span>
                        </a>
                        {/* <button className="dropdown-item delete-item" onClick={() => deleteClientLog(log?._id)}>
                          <div className="dropdown-icon delete-icon">🗑️</div>
                          <span>Delete</span>
                        </button> */}
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
          >
            Previous
          </button>

          <span className="page-info">
            Page {currentPage} of {totalPages}
          </span>

          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}

      <Dialog
        open={modalOpen}
        onClose={closeModal}
        sx={{
          '& .MuiDialog-paper': {
            width: '1200px',
            maxWidth: 'unset',
          },
        }}
      >
        <DialogTitle style={{ textAlign: 'center' }}>{modalTitle}</DialogTitle>

        <DialogTitle style={{ textAlign: 'left', fontSize: `18px`, direction: isRTL ? 'rtl' : 'ltr' }}>{modalContent}</DialogTitle>

      </Dialog >
      <ChatHistoryModal
        isOpen={chatModalOpen}
        onClose={closeChatHistory}
        sessionId={selectedSessionId}
      />
    </div>
  );
};

export default DataTable;
